import { queriesPayerRows } from "@query";
import { WSInfoBox, WSList } from "@wingspanhq/fe-component-library";
import {
  InsuranceCoverage,
  InsuranceCoverageSource,
  InsuranceMonitor,
  InsurancePolicyType
} from "@wingspanhq/wingspan-api-sdk";
import intersection from "lodash/intersection";

import { WSQueries } from "../../../../../../query/WSQuery";
import { useGetNow } from "../../../../../../shared/hooks/useGetNow";
import { selectorSearchRowName } from "../../../../../../shared/selectors/selectorSearchRowName";
import { queriesInsuranceCoverages } from "../../../../../../v3/query/queriesInsuranceCoverages";
import { useFlowUploadDocument } from "../../../../components/FlowUploadDocument";
import {
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { CardPartialMatchSubmitForReview } from "../../components/CardPartialMatchSubmitForReview";
import { CardShareYourInformation } from "../../components/CardShareYourInformation";
import { CardUploadNewDocument } from "../../components/CardUploadNewDocument";
import { CardWingspanInsurance } from "../../components/CardWingspanInsurance";
import { EmptyStateNoMatch } from "../../components/EmptyStateNoMatch";
import { useModalMatchingInProgress } from "../../components/ModalMatchingInProgress";
import { selectorIsInsuranceMonitorInProgress } from "../../selectors/selectorIsInsuranceMonitorInProgress";
import { selectorPolicyTypesFromMonitor } from "../../selectors/selectorPolicyTypesFromMonitor";
import { INSURANCE_COVERAGE_REQUIREMENT_EVENT } from "../utils";

// master policies backed by Wingspan insurance enrollment
const MASTER_POLICY_TYPES: InsurancePolicyType[] = [
  InsurancePolicyType.GENERAL_LIABILITY,
  InsurancePolicyType.OCCUPATIONAL_ACCIDENT,
  InsurancePolicyType.PROFESSIONAL_LIABILITY
];

type Props = {
  requirement: RequirementTableItemData;
  insuranceMonitor: InsuranceMonitor;
  callback: RequirementTableCallback;
};

export const ProcessingDetails: React.FC<Props> = ({
  requirement,
  insuranceMonitor,
  callback
}) => {
  const now = useGetNow();
  const uploadFlow = useFlowUploadDocument({
    onFinish(status) {
      if (status === "success")
        callback(
          INSURANCE_COVERAGE_REQUIREMENT_EVENT.UploadCoverage,
          requirement
        );
    }
  });

  const modalConsent = useModalMatchingInProgress();
  // The client's display name, off the payer *search rows* keyed by
  // `payerAccountId`. It deliberately does not read a `Payer` by id — a v3
  // `Payer.id` is not the payer's account id (it is the composite
  // `<payerAccountId>_<payeeAccountId>` for a linked client, and a standalone id
  // for a platform payer), so the `getPayer({ payerId: payerAccountId })` this
  // used to do 404'd and failed this whole section into the error state. Same
  // lookup as `useRequirementsConfig.getRequirementPayerName`, repeated here
  // because this component is built *inside* that config and can't consume it.
  const payerAccountId = requirement.payerAccountId ?? undefined;
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: { payerAccountId: { eq: payerAccountId } },
      page: { size: 1 }
    },
    { enabled: !!payerAccountId }
  );
  const queryInsuranceCoveragesAll =
    queriesInsuranceCoverages.listInsuranceCoverages.useQuery({
      page: {
        size: 1
      }
    });

  // a configuration may accept more than one policy type, so the
  // partial-match lookup spans all of them
  const acceptedCoverages = insuranceMonitor._configuration?.coverages ?? [];
  const requiredPolicyTypes = selectorPolicyTypesFromMonitor(insuranceMonitor);

  const queryInsuranceCoveragesMatched =
    queriesInsuranceCoverages.listInsuranceCoverages.useQueryAll(
      {
        filter: {
          policyType: {
            anyOf: requiredPolicyTypes
          }
        }
      },
      { enabled: requiredPolicyTypes.length > 0 }
    );

  return (
    <WSQueries
      queries={{
        queryInsuranceCoveragesAll,
        // only gate rendering on the payer-name and match lookups when they
        // actually run — WSQueries waits for every query to succeed, and a
        // disabled one never does
        ...(payerAccountId ? { queryPayerRow } : {}),
        ...(requiredPolicyTypes.length
          ? { queryInsuranceCoveragesMatched }
          : {})
      }}
    >
      {({ queryInsuranceCoveragesAllData }) => {
        const allCoverages = queryInsuranceCoveragesAllData.data ?? [];
        const partialMatchCoverages: InsuranceCoverage[] =
          queryInsuranceCoveragesMatched.flatData ?? [];
        const validPartialMatchCoverages = partialMatchCoverages.filter(
          coverage => !coverage.expirationDate || coverage.expirationDate > now
        );

        const isNotShared =
          selectorIsInsuranceMonitorInProgress(insuranceMonitor) &&
          validPartialMatchCoverages.length > 0;

        const partialMatchLength = partialMatchCoverages.length;
        const isPartialMatch = partialMatchLength > 0;
        const noPoliciesMatch =
          allCoverages.length > 0 && partialMatchLength === 0;

        const matchedToMasterPolicies = intersection(
          requiredPolicyTypes,
          MASTER_POLICY_TYPES
        );

        // Wingspan's own products are plan enrollments, so the enrollment card
        // only helps when the configuration accepts that source
        const acceptsPlanEnrollment = acceptedCoverages.some(coverage =>
          coverage.acceptedSourceTypes?.includes(
            InsuranceCoverageSource.PLAN_ENROLLMENT
          )
        );

        const isWingspanInsuranceAd =
          matchedToMasterPolicies.length > 0 && acceptsPlanEnrollment;

        const payerRow = queryPayerRow.data?.data?.[0];
        const payerName = payerRow ? selectorSearchRowName(payerRow) : "";

        return (
          <WSList gap="L">
            {isNotShared ? (
              <CardShareYourInformation
                payerName={payerName}
                onContinueClick={() =>
                  modalConsent.open({
                    insuranceCoverageMonitorId: insuranceMonitor.id!
                  })
                }
              />
            ) : (
              <>
                {noPoliciesMatch ? (
                  <EmptyStateNoMatch />
                ) : isPartialMatch ? (
                  <WSInfoBox>
                    There are currently {partialMatchLength} policies in your
                    “My resources” section that partially match this
                    requirement. Review the instructions and your options below
                    to take action.
                  </WSInfoBox>
                ) : (
                  <WSInfoBox>
                    There are currently no policies in your “My resources”
                    section that match this requirement. Review the instructions
                    and your options below to take action.
                  </WSInfoBox>
                )}

                {isPartialMatch && (
                  <CardPartialMatchSubmitForReview
                    callback={callback}
                    coverageMonitor={insuranceMonitor}
                    coverages={partialMatchCoverages}
                    requirement={requirement}
                  />
                )}

                {isWingspanInsuranceAd && (
                  <CardWingspanInsurance
                    policyType={matchedToMasterPolicies[0]!}
                  />
                )}

                <CardUploadNewDocument
                  onUploadClick={() => {
                    uploadFlow.open();
                  }}
                />
              </>
            )}
          </WSList>
        );
      }}
    </WSQueries>
  );
};
